import React from 'react'
import { OnChangeEventContext } from 'react-crane'

import {
  BoardGroupFragment,
  PlanGroupsDocument,
  PlanningTerm,
  TermOption,
  useUpdatePlanGroupTermMutation,
  useUpdateTemplateGroupTitleMutation
} from '../../../../../generated/graphql'
import PlanGroupTitle from './PlanGroupTitle'
import useNotification from '../../../../../lib/hooks/useNotification'
import useAppSettings from '../../../../../lib/hooks/useAppSettings'
import useModal from '../../../../../lib/hooks/useModal'
import { ModalName } from '../../../../../types'
import { GET_PLAN_ALERTS } from '../../../../../graphql/plans'

type PlanGroupTitleWrapperProps = {
  /** Number of total alerts cross items for group */
  alertCount?: number
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Event callback when Add Comment is clicked */
  onAddComment: () => void
  /** Event callback when Delete Term is clicked */
  onDelete: () => void
  /** Indicates if this plan/template is read-only */
  readOnly?: boolean
  /** Whether the add comment field is visible */
  showAddComment: boolean
  /** Optional list of configured term options to use for template term naming */
  termOptions?: Array<TermOption>
  /** List of terms to display in the term select on a plan */
  terms?: Array<PlanningTerm>
}

const defaultProps: Pick<
  PlanGroupTitleWrapperProps,
  'alertCount' | 'readOnly' | 'terms' | 'termOptions'
> = {
  alertCount: 0,
  readOnly: false,
  termOptions: undefined,
  terms: undefined
}

const PlanGroupTitleWrapper = ({
  alertCount,
  group,
  isTemplate,
  onAddComment,
  onDelete,
  readOnly,
  showAddComment,
  termOptions,
  terms
}: PlanGroupTitleWrapperProps) => {
  const { id, planId, title } = group
  const { showErrorNotif } = useNotification()
  const { sectionFieldText, text } = useAppSettings()
  const creditsLabel = sectionFieldText.credits
  const termLabel = text.term
  const { showModal } = useModal()

  const [updateTemplateGroupTitle] = useUpdateTemplateGroupTitleMutation({
    onError: () => {
      showErrorNotif(`Could not change ${termLabel.toLowerCase()} name`)
    }
  })
  const [updateTerm] = useUpdatePlanGroupTermMutation({
    onError: () => {
      showErrorNotif(`Could not assign ${termLabel.toLowerCase()} to group.`)
    }
  })

  // Update externalTermId and title when changing a term, title is only used for backup
  const onTermChange = (e: OnChangeEventContext) => {
    const term = e.value as PlanningTerm

    updateTerm({
      variables: {
        input: {
          id,
          externalTermId: term.termCode,
          title: term.description
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updatePlanGroupTerm: {
          __typename: 'PlanGroup',
          ...group,
          externalTermId: term.termCode
        }
      },
      refetchQueries: [
        // Because alerts can change depending on the term (aka: inactive course or course unavailable),
        // when the term changes, refetch the alerts so that they get re-determined in the api and displayed on the planItemCard
        {
          query: GET_PLAN_ALERTS,
          variables: { planId }
        }
      ],
      update: (cache) => {
        // Similar to the comment in refetchQueries, evict cache for the planItems in this group, so that
        // the next time the planItemDetails modal is opened, the alert will be shown (or not shown) as necessary
        group.items.forEach((item) => {
          cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'planItem',
            args: { id: item.id }
          })
        })
      }
    })
  }

  const onTermOptionChange = (e: OnChangeEventContext) => {
    const termOption = e.value as TermOption

    // No need to change anything if the term selected is the same as the current title
    if (termOption && !title.includes(termOption.value)) {
      const input = {
        id,
        title: termOption.value,
        termType: termOption.type
      }
      updateTemplateGroupTitle({
        variables: { input },
        refetchQueries: [
          // Because of the re-sequencing that happens when a different term option is selected,
          // refetch groups so the titles are accurate.  The logic to re-sequence is complicated
          // enough that it should stay in the backend.
          {
            query: PlanGroupsDocument,
            variables: { planId }
          }
        ]
      })
    }
  }

  const handleMove = () => {
    showModal(ModalName.MovePlanGroup, { groupId: group.id, planId: group.planId, isTemplate })
  }

  return (
    <PlanGroupTitle
      key={`${id}:${group.title}`}
      alertCount={alertCount}
      creditsLabel={creditsLabel}
      group={group}
      isTemplate={isTemplate}
      onAddComment={onAddComment}
      onDelete={onDelete}
      onMove={handleMove}
      onTermChange={onTermChange}
      onTermOptionChange={onTermOptionChange}
      readOnly={readOnly}
      showAddComment={showAddComment}
      termLabel={termLabel}
      termOptions={termOptions}
      terms={terms}
    />
  )
}

PlanGroupTitleWrapper.defaultProps = defaultProps

export default PlanGroupTitleWrapper
